import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Database, 
  Plus, 
  Edit2, 
  Trash2, 
  RefreshCw,
  X,
  Check,
  Search,
  Settings2,
  Layers,
} from 'lucide-react';
import { GetDropdownData } from './apis/get';
import { PostUpdateDropdownData, PostDeleteDropdownData, PostUpdatePreferenceValue, PostDeletePreferenceValue } from './apis/post';
import toast from 'react-hot-toast';
import LoaderService from '../services/LoaderService';

interface PreferenceValue {
    id?: number;
    value?: string;
    color?: string | null;
    icon_url?: string | null;
    min?: number;
    max?: number;
    start?: number;
    end?: number;
}

interface Preference {
    preferenceId: number;
    title: string;
    type: string;
    icon: string;
    values: PreferenceValue[];
}

interface PreferenceCategory {
    category: string;
    preferences: Preference[];
}

const DropdownData = () => {
    const [loading, setLoading] = useState(true);
    const [rawDropdownData, setRawDropdownData] = useState<Record<string, string[]>>({});
    const [preferences, setPreferences] = useState<PreferenceCategory[]>([]);
    const [activeTab, setActiveTab] = useState<'general' | 'preferences'>('general');
    const [isEditing, setIsEditing] = useState<{category: string, index: number, value: string} | null>(null);
    const [newValues, setNewValues] = useState<Record<string, string>>({});
    const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
    const [prefSearchQueries, setPrefSearchQueries] = useState<Record<string, string>>({});
    const [activeGeneralFilter, setActiveGeneralFilter] = useState<string>('all');
    const [activePreferenceFilter, setActivePreferenceFilter] = useState<string>('all');

    // Preference editing states
    const [prefEditing, setPrefEditing] = useState<{prefId: number, valueId: number, value: string} | null>(null);
    const [prefNewValues, setPrefNewValues] = useState<Record<number, string>>({});

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await GetDropdownData();
            if (response.status === true || response.status === 'success') {
                setRawDropdownData(response.data || {});
                setPreferences(response.preferences || []);
            }
        } catch (error) {
            console.error("Failed to fetch dropdown data", error);
            toast.error("Failed to load master data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ========== GENERAL DATA HANDLERS ==========
    const handleAddValue = async (category: string) => {
        const val = newValues[category]?.trim();
        if (!val) return;
        
        try {
            LoaderService.show();
            const response = await (PostUpdateDropdownData as any)(category, val);
            if (response.status === true || response.status === 'success') {
                toast.success(`"${val}" added to ${category}`);
                setNewValues(prev => ({ ...prev, [category]: "" }));
                await fetchData();
            } else {
                toast.error(response.message || response.detail || "Failed to add value");
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            LoaderService.hide();
        }
    };

    const handleUpdateValue = async () => {
        if (!isEditing) return;
        const { category, index, value } = isEditing;
        const trimmedValue = value.trim();
        const oldValue = rawDropdownData[category][index];

        if (oldValue === trimmedValue) {
            setIsEditing(null);
            return;
        }

        try {
            LoaderService.show();
            const response = await (PostUpdateDropdownData as any)(category, trimmedValue, oldValue);
            if (response.status === true || response.status === 'success') {
                toast.success("Updated successfully");
                setIsEditing(null);
                await fetchData();
            } else {
                toast.error(response.message || response.detail || "Update failed");
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            LoaderService.hide();
        }
    };

    const handleDeleteValue = async (category: string, val: string) => {
        if (!window.confirm(`Delete "${val}" from ${category}?`)) return;

        try {
            LoaderService.show();
            const response = await PostDeleteDropdownData(category, val);
            if (response.status === true || response.status === 'success') {
                toast.success("Removed successfully");
                await fetchData();
            } else {
                toast.error(response.message || response.detail || "Delete failed");
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            LoaderService.hide();
        }
    };

    // ========== PREFERENCE DATA HANDLERS ==========
    const handleAddPrefValue = async (preferenceId: number, prefTitle: string) => {
        const val = prefNewValues[preferenceId]?.trim();
        if (!val) return;

        try {
            LoaderService.show();
            const response = await (PostUpdatePreferenceValue as any)(preferenceId, val);
            if (response.status === true || response.status === 'success') {
                toast.success(`"${val}" added to ${prefTitle}`);
                setPrefNewValues(prev => ({ ...prev, [preferenceId]: "" }));
                await fetchData();
            } else {
                toast.error(response.message || response.detail || "Failed to add preference value");
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            LoaderService.hide();
        }
    };

    const handleUpdatePrefValue = async () => {
        if (!prefEditing) return;
        const { prefId, value } = prefEditing;
        const trimmedValue = value.trim();

        // Find the old value
        let oldValue = '';
        for (const cat of preferences) {
            for (const pref of cat.preferences) {
                if (pref.preferenceId === prefId) {
                    const found = pref.values.find(v => v.id === prefEditing.valueId);
                    if (found) oldValue = found.value || '';
                }
            }
        }

        if (oldValue === trimmedValue) {
            setPrefEditing(null);
            return;
        }

        try {
            LoaderService.show();
            const response = await (PostUpdatePreferenceValue as any)(prefId, trimmedValue, oldValue);
            if (response.status === true || response.status === 'success') {
                toast.success("Preference value updated");
                setPrefEditing(null);
                await fetchData();
            } else {
                toast.error(response.message || response.detail || "Update failed");
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            LoaderService.hide();
        }
    };

    const handleDeletePrefValue = async (preferenceId: number, val: string, prefTitle: string) => {
        if (!window.confirm(`Delete "${val}" from ${prefTitle}?`)) return;

        try {
            LoaderService.show();
            const response = await (PostDeletePreferenceValue as any)(preferenceId, val);
            if (response.status === true || response.status === 'success') {
                toast.success("Preference value removed");
                await fetchData();
            } else {
                toast.error(response.message || response.detail || "Delete failed");
            }
        } catch (error) {
            toast.error("An error occurred");
        } finally {
            LoaderService.hide();
        }
    };

    // ========== COMPUTED ==========
    const categories = Object.keys(rawDropdownData).map(key => ({
        id: key,
        name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        items: rawDropdownData[key]
    }));

    const totalGeneralItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
    const totalPrefItems = preferences.reduce((sum, cat) => sum + cat.preferences.reduce((s, p) => s + p.values.length, 0), 0);

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'options': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
            case 'slider': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
            case 'checkbox': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
            default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
        }
    };

    const filteredCategories = activeGeneralFilter === 'all'
        ? categories
        : categories.filter(cat => cat.id === activeGeneralFilter || cat.name.toLowerCase().includes(activeGeneralFilter));

    const filteredPreferences = activePreferenceFilter === 'all'
        ? preferences
        : preferences.filter(prefCat => prefCat.category === activePreferenceFilter || prefCat.category.toLowerCase().includes(activePreferenceFilter));

    return (
        <div className="min-h-screen bg-transparent space-y-8 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-white tracking-tight uppercase italic">
                        Dropdown <span className="text-blue-500">Repository</span>
                    </h1>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-[0.3em] mt-2">
                        Unified Master Data Management
                    </p>
                </div>

                <button 
                    onClick={fetchData}
                    disabled={loading}
                    className="h-11 px-6 bg-slate-900 border border-slate-800 text-white text-[10px] font-black rounded-xl hover:bg-slate-800 transition-all uppercase tracking-widest flex items-center gap-2 group"
                >
                    <RefreshCw size={14} className={`${loading ? 'animate-spin' : 'group-hover:rotate-180'} transition-transform duration-500`} />
                    {loading ? 'RE-SYNCING...' : 'Refresh All Clusters'}
                </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 bg-slate-900/50 p-1.5 rounded-2xl border border-slate-800 w-fit">
                <button
                    onClick={() => setActiveTab('general')}
                    className={`flex items-center gap-2.5 px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${
                        activeTab === 'general'
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                            : 'text-slate-500 hover:text-white hover:bg-slate-800/50'
                    }`}
                >
                    <Database size={14} />
                    General Data
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-[9px] font-black ${
                        activeTab === 'general' 
                            ? 'bg-white/20 text-white' 
                            : 'bg-slate-800 text-slate-500'
                    }`}>
                        {categories.length}
                    </span>
                </button>
                <button
                    onClick={() => setActiveTab('preferences')}
                    className={`flex items-center gap-2.5 px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${
                        activeTab === 'preferences'
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                            : 'text-slate-500 hover:text-white hover:bg-slate-800/50'
                    }`}
                >
                    <Settings2 size={14} />
                    Preference Data
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-[9px] font-black ${
                        activeTab === 'preferences' 
                            ? 'bg-white/20 text-white' 
                            : 'bg-slate-800 text-slate-500'
                    }`}>
                        {preferences.length}
                    </span>
                </button>
            </div>

            {/* ==================== GENERAL DATA TAB ==================== */}
            {activeTab === 'general' && (
                <div className="space-y-12">
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            onClick={() => setActiveGeneralFilter('all')}
                            className={`px-4 py-2 rounded-full border text-[11px] font-black transition-all ${activeGeneralFilter === 'all' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                        >
                            All
                        </button>
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveGeneralFilter(cat.id)}
                                className={`px-4 py-2 rounded-full border text-[11px] font-black transition-all ${activeGeneralFilter === cat.id ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {filteredCategories.map((cat, idx) => {
                        const query = searchQueries[cat.id] || "";
                        const filteredItems = cat.items.filter(item => 
                            item.toLowerCase().includes(query.toLowerCase())
                        );

                        return (
                            <motion.section 
                                key={cat.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-slate-900/40 border border-slate-800 rounded-[2rem] overflow-hidden"
                            >
                                {/* Category Banner */}
                                <div className="p-8 border-b border-slate-800 bg-slate-950/40 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-500 shadow-lg shadow-blue-500/5">
                                            <Database size={24} />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-black text-white uppercase italic tracking-tight">{cat.name}</h2>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
                                                    {cat.items.length} Available Nodes
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row items-center gap-4">
                                        {/* Inline Search */}
                                        <div className="relative w-full sm:w-64">
                                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={14} />
                                            <input 
                                                type="text" 
                                                placeholder="FILTER NODES..."
                                                value={searchQueries[cat.id] || ""}
                                                onChange={(e) => setSearchQueries(prev => ({ ...prev, [cat.id]: e.target.value }))}
                                                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-[10px] font-black text-white placeholder:text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30 transition-all uppercase tracking-widest"
                                            />
                                        </div>

                                        {/* Inline Add */}
                                        <div className="flex w-full sm:w-auto gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="NEW NODE NAME..."
                                                value={newValues[cat.id] || ""}
                                                onChange={(e) => setNewValues(prev => ({ ...prev, [cat.id]: e.target.value }))}
                                                onKeyPress={(e) => e.key === 'Enter' && handleAddValue(cat.id)}
                                                className="flex-1 sm:w-48 bg-slate-950 border border-slate-800 rounded-xl py-2.5 px-4 text-[10px] font-black text-white placeholder:text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 transition-all uppercase tracking-widest"
                                            />
                                            <button 
                                                onClick={() => handleAddValue(cat.id)}
                                                disabled={!newValues[cat.id]?.trim()}
                                                className="h-10 px-4 bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-600/10 flex items-center justify-center hover:bg-emerald-500 transition-all active:scale-95 disabled:opacity-30"
                                            >
                                                <Plus size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Nodes Grid */}
                                <div className="p-8">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {filteredItems.map((val, vIdx) => {
                                            const isThisEditing = isEditing?.category === cat.id && isEditing?.index === vIdx;

                                            return (
                                                <div 
                                                    key={vIdx}
                                                    className={`p-3 rounded-2xl border transition-all duration-300 group ${
                                                        isThisEditing 
                                                        ? 'bg-blue-600/10 border-blue-500/50 shadow-lg shadow-blue-500/10' 
                                                        : 'bg-slate-950/30 border-slate-800 hover:border-slate-700 hover:bg-slate-900/50'
                                                    }`}
                                                >
                                                    {isThisEditing ? (
                                                        <div className="flex items-center gap-2">
                                                            <input 
                                                                autoFocus
                                                                className="flex-1 bg-transparent border-none text-[11px] font-black text-blue-400 focus:outline-none uppercase tracking-widest"
                                                                value={isEditing.value}
                                                                onChange={(e) => setIsEditing({...isEditing, value: e.target.value})}
                                                                onKeyPress={(e) => e.key === 'Enter' && handleUpdateValue()}
                                                            />
                                                            <div className="flex gap-1 shrink-0">
                                                                <button onClick={handleUpdateValue} className="p-1 px-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors">
                                                                    <Check size={12}/>
                                                                </button>
                                                                <button onClick={() => setIsEditing(null)} className="p-1 px-1.5 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700">
                                                                    <X size={12}/>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <div className="w-1.5 h-1.5 rounded-full bg-slate-800 group-hover:bg-blue-500 transition-colors" />
                                                                <span className="text-[11px] font-bold text-slate-400 group-hover:text-white truncate uppercase tracking-tight transition-colors">
                                                                    {val}
                                                                </span>
                                                            </div>
                                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                                                                <button 
                                                                    onClick={() => setIsEditing({category: cat.id, index: vIdx, value: val})}
                                                                    className="p-1.5 hover:bg-blue-500/10 text-slate-700 hover:text-blue-400 rounded-lg transition-colors"
                                                                >
                                                                    <Edit2 size={12} />
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleDeleteValue(cat.id, val)}
                                                                    className="p-1.5 hover:bg-red-500/10 text-slate-700 hover:text-red-400 rounded-lg transition-colors"
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        
                                        {filteredItems.length === 0 && (
                                            <div className="col-span-full py-8 text-center border-2 border-dashed border-slate-900 rounded-3xl">
                                                <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic">No nodes found in this cluster</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.section>
                        );
                    })}
                </div>
            )}

            {/* ==================== PREFERENCE DATA TAB ==================== */}
            {activeTab === 'preferences' && (
                <div className="space-y-12">
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            onClick={() => setActivePreferenceFilter('all')}
                            className={`px-4 py-2 rounded-full border text-[11px] font-black transition-all ${activePreferenceFilter === 'all' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                        >
                            All
                        </button>
                        {preferences.map((prefCat) => (
                            <button
                                key={prefCat.category}
                                onClick={() => setActivePreferenceFilter(prefCat.category)}
                                className={`px-4 py-2 rounded-full border text-[11px] font-black transition-all ${activePreferenceFilter === prefCat.category ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                            >
                                {prefCat.category}
                            </button>
                        ))}
                    </div>
                    {filteredPreferences.map((prefCat, catIdx) => (
                        <motion.section
                            key={prefCat.category}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: catIdx * 0.1 }}
                            className="bg-slate-900/40 border border-slate-800 rounded-[2rem] overflow-hidden"
                        >
                            {/* Category Header */}
                            <div className="p-8 border-b border-slate-800 bg-slate-950/40 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-purple-500 shadow-lg shadow-purple-500/5">
                                        <Layers size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-white uppercase italic tracking-tight">{prefCat.category}</h2>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] font-black text-purple-500 uppercase tracking-widest bg-purple-500/10 px-2 py-0.5 rounded-full border border-purple-500/20">
                                                {prefCat.preferences.length} Preferences
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Search for this category */}
                                <div className="relative w-full sm:w-64">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={14} />
                                    <input 
                                        type="text" 
                                        placeholder="FILTER PREFERENCES..."
                                        value={prefSearchQueries[prefCat.category] || ""}
                                        onChange={(e) => setPrefSearchQueries(prev => ({ ...prev, [prefCat.category]: e.target.value }))}
                                        className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-[10px] font-black text-white placeholder:text-slate-700 focus:outline-none focus:ring-1 focus:ring-purple-500/30 transition-all uppercase tracking-widest"
                                    />
                                </div>
                            </div>

                            {/* Preferences List */}
                            <div className="p-8 space-y-6">
                                {prefCat.preferences
                                    .filter(pref => {
                                        const q = (prefSearchQueries[prefCat.category] || "").toLowerCase();
                                        if (!q) return true;
                                        if (pref.title.toLowerCase().includes(q)) return true;
                                        return pref.values.some(v => v.value?.toLowerCase().includes(q));
                                    })
                                    .map((pref) => (
                                    <div 
                                        key={pref.preferenceId} 
                                        className="bg-slate-950/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-all"
                                    >
                                        {/* Preference Header */}
                                        <div className="p-5 border-b border-slate-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center overflow-hidden">
                                                    <img 
                                                        src={pref.icon} 
                                                        alt={pref.title}
                                                        className="w-6 h-6 object-contain"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-black text-white uppercase tracking-tight">{pref.title}</h3>
                                                    <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">
                                                        ID: {pref.preferenceId}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getTypeColor(pref.type)}`}>
                                                    {pref.type}
                                                </span>
                                                <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest bg-slate-900 px-2.5 py-1 rounded-full border border-slate-800">
                                                    {pref.values.length} values
                                                </span>
                                            </div>
                                        </div>

                                        {/* Preference Values */}
                                        <div className="p-5">
                                            {pref.type === 'slider' ? (
                                                /* Slider type - read-only display */
                                                <div className="flex flex-wrap items-center gap-4">
                                                    {pref.values.map((v, i) => (
                                                        <div key={i} className="flex items-center gap-6 bg-slate-900/50 border border-slate-800 rounded-xl px-5 py-3">
                                                            <div className="text-center">
                                                                <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">Min</div>
                                                                <div className="text-lg font-black text-blue-400">{v.min}</div>
                                                            </div>
                                                            <div className="w-px h-8 bg-slate-800" />
                                                            <div className="text-center">
                                                                <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">Start</div>
                                                                <div className="text-lg font-black text-emerald-400">{v.start}</div>
                                                            </div>
                                                            <div className="text-slate-700 font-black">—</div>
                                                            <div className="text-center">
                                                                <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">End</div>
                                                                <div className="text-lg font-black text-emerald-400">{v.end}</div>
                                                            </div>
                                                            <div className="w-px h-8 bg-slate-800" />
                                                            <div className="text-center">
                                                                <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">Max</div>
                                                                <div className="text-lg font-black text-red-400">{v.max}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                /* Options / Checkbox type - editable */
                                                <>
                                                    {/* Add new value input */}
                                                    <div className="flex gap-2 mb-4">
                                                        <input 
                                                            type="text" 
                                                            placeholder={`ADD NEW VALUE TO ${pref.title.toUpperCase()}...`}
                                                            value={prefNewValues[pref.preferenceId] || ""}
                                                            onChange={(e) => setPrefNewValues(prev => ({ ...prev, [pref.preferenceId]: e.target.value }))}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleAddPrefValue(pref.preferenceId, pref.title)}
                                                            className="flex-1 bg-slate-900/50 border border-slate-800 rounded-xl py-2.5 px-4 text-[10px] font-black text-white placeholder:text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 transition-all uppercase tracking-widest"
                                                        />
                                                        <button 
                                                            onClick={() => handleAddPrefValue(pref.preferenceId, pref.title)}
                                                            disabled={!prefNewValues[pref.preferenceId]?.trim()}
                                                            className="h-10 px-4 bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-600/10 flex items-center justify-center hover:bg-emerald-500 transition-all active:scale-95 disabled:opacity-30"
                                                        >
                                                            <Plus size={16} />
                                                        </button>
                                                    </div>

                                                    {/* Values grid */}
                                                    <div className="flex flex-wrap gap-2">
                                                        {pref.values.map((v) => {
                                                            const isPrefEditing = prefEditing?.prefId === pref.preferenceId && prefEditing?.valueId === v.id;

                                                            return (
                                                                <div 
                                                                    key={v.id} 
                                                                    className={`group flex items-center gap-2 border rounded-xl px-3 py-2 transition-all ${
                                                                        isPrefEditing
                                                                            ? 'bg-blue-600/10 border-blue-500/50 shadow-lg shadow-blue-500/10'
                                                                            : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                                                                    }`}
                                                                >
                                                                    {isPrefEditing ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <input 
                                                                                autoFocus
                                                                                className="bg-transparent border-none text-[11px] font-black text-blue-400 focus:outline-none uppercase tracking-widest w-32"
                                                                                value={prefEditing.value}
                                                                                onChange={(e) => setPrefEditing({...prefEditing, value: e.target.value})}
                                                                                onKeyPress={(e) => e.key === 'Enter' && handleUpdatePrefValue()}
                                                                            />
                                                                            <div className="flex gap-1 shrink-0">
                                                                                <button onClick={handleUpdatePrefValue} className="p-1 px-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors">
                                                                                    <Check size={10}/>
                                                                                </button>
                                                                                <button onClick={() => setPrefEditing(null)} className="p-1 px-1.5 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700">
                                                                                    <X size={10}/>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-slate-700 group-hover:bg-blue-500 transition-colors" />
                                                                            <span className="text-[11px] font-bold text-slate-400 group-hover:text-white uppercase tracking-tight transition-colors">
                                                                                {v.value}
                                                                            </span>
                                                                            <span className="text-[8px] font-black text-slate-700 ml-1">
                                                                                #{v.id}
                                                                            </span>
                                                                            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all shrink-0 ml-1">
                                                                                <button 
                                                                                    onClick={() => setPrefEditing({prefId: pref.preferenceId, valueId: v.id!, value: v.value || ''})}
                                                                                    className="p-1 hover:bg-blue-500/10 text-slate-700 hover:text-blue-400 rounded-lg transition-colors"
                                                                                >
                                                                                    <Edit2 size={10} />
                                                                                </button>
                                                                                <button 
                                                                                    onClick={() => handleDeletePrefValue(pref.preferenceId, v.value || '', pref.title)}
                                                                                    className="p-1 hover:bg-red-500/10 text-slate-700 hover:text-red-400 rounded-lg transition-colors"
                                                                                >
                                                                                    <Trash2 size={10} />
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {prefCat.preferences.filter(pref => {
                                    const q = (prefSearchQueries[prefCat.category] || "").toLowerCase();
                                    if (!q) return true;
                                    if (pref.title.toLowerCase().includes(q)) return true;
                                    return pref.values.some(v => v.value?.toLowerCase().includes(q));
                                }).length === 0 && (
                                    <div className="py-8 text-center border-2 border-dashed border-slate-900 rounded-3xl">
                                        <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic">No preferences found matching filter</span>
                                    </div>
                                )}
                            </div>
                        </motion.section>
                    ))}

                    {preferences.length === 0 && (
                        <div className="py-16 text-center border-2 border-dashed border-slate-900 rounded-3xl">
                            <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic">No preference data available</span>
                        </div>
                    )}
                </div>
            )}
            
            {/* Action Summary */}
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900/80 backdrop-blur-xl border border-slate-800 px-8 py-4 rounded-full shadow-2xl z-50 flex items-center gap-8">
                <div className="flex items-center gap-3">
                   <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                   <span className="text-[10px] font-black text-white uppercase tracking-widest whitespace-nowrap">Global System Synced</span>
                </div>
                <div className="w-px h-6 bg-slate-800" />
                <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                   {activeTab === 'general' 
                       ? `${categories.length} Data Clusters • ${totalGeneralItems} Total Nodes`
                       : `${preferences.length} Categories • ${totalPrefItems} Total Values`
                   }
                </div>
            </div>
        </div>
    );
};

export default DropdownData;
